import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Badge, Button, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axios from '../../services/axiosConfig';
import { formatDate, formatDateRange } from '../../utils/dateUtils';

const UpcomingEvents = ({ limit = 3 }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUpcoming = async () => {
      setLoading(true);
      try {
        const today = new Date();
        const params = new URLSearchParams();
        params.append('dateFrom', today.toISOString().slice(0, 10));
        params.append('limit', limit);
        const res = await axios.get(`/api/events/search?${params.toString()}`);
        setEvents(res.data.events || []);
      } catch (err) {
        setError('Errore nel caricamento dei prossimi eventi');
      }
      setLoading(false);
    };
    fetchUpcoming();
  }, [limit]);

  if (loading) return <Spinner animation="border" className="my-3" />;
  if (error) return <div className="text-danger">{error}</div>;
  if (!events.length) return <div>Nessun evento in programma.</div>;

  return (
    <div>
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4 gap-2">
        <h4 className="mb-0" style={{ color: '#006e39', fontWeight: '600' }}>
          <i className="bi bi-calendar-event me-2"></i>
          Prossimi Eventi in Programma
        </h4>
        <Badge bg="light" text="dark" style={{ fontSize: '0.9rem' }}>
          {events.length} {events.length === 1 ? 'evento' : 'eventi'}
        </Badge>
      </div>
      <Row>
        {events.map(event => {
          // Check if registrations are open, including late registrations
          const now = new Date();
          const eventDate = new Date(event.eventDate || event.event_date);
          const registrationDeadline = event.registrationDeadline
            ? new Date(event.registrationDeadline)
            : (event.registration_deadline ? new Date(event.registration_deadline) : null);
          const allowLateRegistration = event.allowLateRegistration === true || event.allow_late_registration === true;

          const isEventStarted = !isNaN(eventDate.getTime()) && now > eventDate;
          const isBeforeDeadline = registrationDeadline ? now <= registrationDeadline : true;
          const isLateRegistrationOpen = !isBeforeDeadline && allowLateRegistration && !isEventStarted;
          const isRegistrationOpen = !isEventStarted && (isBeforeDeadline || isLateRegistrationOpen);

          const badgeClass = isLateRegistrationOpen ? 'bg-warning text-dark' : (isRegistrationOpen ? 'bg-success' : 'bg-danger');
          const badgeLabel = isLateRegistrationOpen ? 'Tardive' : (isRegistrationOpen ? 'Aperte' : 'Chiuse');
          const badgeIcon = isLateRegistrationOpen
            ? 'bi-exclamation-triangle'
            : (isRegistrationOpen ? 'bi-check-circle' : 'bi-x-circle');
          
          return (
          <Col xs={12} sm={6} md={4} key={event._id || event.id} className="mb-4">
            <Link to={`/association/events/${event._id || event.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
              <Card className="h-100 shadow-sm border-0" style={{ borderRadius: '12px', transition: 'transform 0.2s', cursor: 'pointer' }} 
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>
                <Card.Body>
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <Card.Title style={{ color: '#006e39', fontWeight: '600', fontSize: '1.1rem' }}>
                    {event.title}
                  </Card.Title>
                  <span className={`badge ${badgeClass}`} 
                        style={{ fontSize: '0.7rem', padding: '4px 8px' }}>
                    <i className={`bi ${badgeIcon} me-1`}></i>
                    {badgeLabel}
                  </span>
                </div>
                
                <div className="mb-3">
                  <div className="mb-2">
                    <i className="bi bi-calendar3 text-primary me-2"></i>
                    <strong>Data:</strong> {formatDateRange(event.eventDate || event.event_date, event.eventEndDate || event.event_end_date || event.eventDate || event.event_date)}
                  </div>
                  {(event.startTime || event.start_time) && (
                    <div className="mb-2">
                      <i className="bi bi-clock text-info me-2"></i>
                      <strong>Ora:</strong> {event.startTime || event.start_time}
                    </div>
                  )}
                  {registrationDeadline && (
                    <div className="mb-2">
                      <i className="bi bi-calendar-x text-warning me-2"></i>
                      <strong>Chiusura iscrizioni:</strong> {formatDate(registrationDeadline)}
                    </div>
                  )}
                  <div className="mb-2">
                    <i className="bi bi-geo-alt-fill text-danger me-2"></i>
                    <strong>Località:</strong> {event.location || 'Non specificata'}
                  </div>
                </div>
                
                <div className="mb-3 d-flex flex-wrap gap-2">
                  {event.category && <Badge bg="primary" style={{ fontSize: '0.8rem' }}>{event.category}</Badge>}
                  {event.sda && <Badge bg="info" style={{ fontSize: '0.8rem' }}>{event.sda}</Badge>}
                  {(() => {
                    const scopeLabels = { national: 'Nazionale', regional: 'Regionale', territorial: 'Territoriale' };
                    const scopeBg = { national: 'danger', regional: 'warning', territorial: 'secondary' };
                    const normalizedScope = String(event.scope || '').toLowerCase();
                    if (!scopeLabels[normalizedScope]) return null;
                    return <Badge bg={scopeBg[normalizedScope]} style={{ fontSize: '0.8rem' }}>{scopeLabels[normalizedScope]}</Badge>;
                  })()}
                </div>
                
                <Card.Text className="text-muted" style={{ fontSize: '0.9rem' }}>
                  {event.description && event.description.length > 100
                    ? `${event.description.substring(0, 100)}...`
                    : event.description || 'Nessuna descrizione disponibile'}
                </Card.Text>
                
                {/* <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
                  {event.price !== undefined && event.price !== null && (
                    <small className="text-muted">
                      <i className="bi bi-currency-euro me-1"></i>
                      {event.price === 0 ? 'Gratuito' : `€${Number(event.price).toFixed(2)}`}
                    </small>
                  )}
                </div> */}
              </Card.Body>
              <Card.Footer className="bg-light border-0" style={{ borderRadius: '0 0 12px 12px' }}>
                <Button 
                  style={{ backgroundColor: '#006e39', borderColor: '#006e39', width: '100%', borderRadius: '8px' }}
                  size="sm"
                >
                  <i className="bi bi-eye me-2"></i> Visualizza Dettagli
                </Button>
              </Card.Footer>
            </Card>
            </Link>
          </Col>
          );
        })}
      </Row>
    </div>
  );
};

export default UpcomingEvents;
